import React, { useMemo } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { fmtEur } from '../../utils/formatArcaNumbers';
import { projectByYear, projectComparisonByYear } from '../../utils/monthlyProjection';
import { PlanReturnView, periodRangeLabel, rateOf, ageNote } from '../../utils/transferReturns';
import {
	SeriesLabel,
	SeriesName,
	legendStats,
	legendStatsShort,
	periodWithRange
} from './projectionLegend';

interface CustomTooltipProps {
	active?: boolean;
	payload?: { value: number; dataKey: string }[];
	label?: number;
	arcaName: SeriesName;
	currentName: SeriesName;
	arcaBetter: boolean;
}

/**
 * Lo que tiene que verse al pasar por encima es la cifra: el nombre se puede recortar,
 * el importe nunca. El del plan de Arca va en rojo si acaba perdiendo, como la curva.
 */
const CustomTooltip: React.FC<CustomTooltipProps> = ({
	active,
	payload,
	label,
	arcaName,
	currentName,
	arcaBetter
}) => {
	if (!active || !payload?.length) return null;
	const arca = payload.find((p) => p.dataKey === 'plan');
	const current = payload.find((p) => p.dataKey === 'bank');
	return (
		<div className="bg-popover border border-border rounded-lg shadow-md px-3 py-2 text-xs space-y-1 min-w-0 max-w-[320px]">
			<p className="font-semibold text-foreground">{label === 0 ? 'Hoy' : `Año ${label}`}</p>
			{arca && (
				<p data-series="arca" className="flex items-center gap-1.5 min-w-0 text-foreground">
					<span
						className={`w-2 h-2 shrink-0 rounded-full ${arcaBetter ? 'bg-primary' : 'bg-destructive'}`}
					/>
					<SeriesLabel name={arcaName} suffix=":" />
					<span
						data-value
						className={`shrink-0 font-medium ${arcaBetter ? 'text-primary' : 'text-destructive'}`}
					>
						{fmtEur(arca.value)}
					</span>
				</p>
			)}
			{current && (
				<p
					data-series="current"
					className="flex items-center gap-1.5 min-w-0 text-muted-foreground"
				>
					<span className="w-2 h-2 shrink-0 rounded-full bg-muted-foreground/40" />
					<SeriesLabel name={currentName} suffix=":" />
					<span data-value className="shrink-0">
						{fmtEur(current.value)}
					</span>
				</p>
			)}
		</div>
	);
};

interface TransferProjectionCardProps {
	/** Importe a proyectar (importe traspasado = saldo × %). */
	balance: number;
	/** Años hasta la jubilación (horizonte de la proyección). */
	years: number;
	/** Aportación mensual actual, aplicada a ambos escenarios. */
	monthlyContribution: number;
	/** Rentabilidad del plan de Arca, decidida por comparsa (`/plan-returns`). */
	arca: PlanReturnView;
	/** La del plan del usuario; `null` si no hay datos: se oculta su curva con un aviso. */
	origin: PlanReturnView | null;
	/** Falso si los dos datos son de periodos distintos: no se dice «el mismo periodo». */
	samePeriod: boolean;
	arcaPlanName: string;
	/** Nombre corto del plan de Arca (la gestora, como en el funnel: «Horos AM»). */
	arcaShortName?: string;
	currentPlanName: string;
	translate: (key: string) => string;
}

/**
 * Gráfica de proyección del importe traspasado hasta la jubilación: "con Arca" vs
 * "si se queda en tu plan actual". Es un componente controlado — el horizonte (years)
 * y la aportación mensual llegan por props (los edita la card de ajustes de al lado).
 */
const TransferProjectionCard: React.FC<TransferProjectionCardProps> = ({
	balance,
	years,
	monthlyContribution,
	arca,
	origin,
	samePeriod,
	arcaPlanName,
	arcaShortName,
	currentPlanName,
	translate
}) => {
	// Mes a mes con la tasa MENSUAL y NETA de comparsa (aportación a final de mes): no se resta
	// la comisión (ya está descontada) ni se suaviza el dato de 1 año (guía v3.1).
	const data: { year: number; plan: number; bank?: number }[] = useMemo(
		() =>
			origin
				? projectComparisonByYear(balance, monthlyContribution, rateOf(arca), rateOf(origin), years)
				: projectByYear(balance, monthlyContribution, rateOf(arca), years).map((v, year) => ({
						year,
						plan: Math.round(v)
					})),
		[balance, monthlyContribution, arca, origin, years]
	);

	const last = data[data.length - 1];
	const arcaFinal = last.plan;
	const currentFinal = last.bank ?? 0;

	// Objetividad: si el plan elegido acaba por debajo del actual, se pinta en rojo.
	const arcaBetter = !origin || arcaFinal >= currentFinal;
	const originNoData =
		translate('individual.transferProjection.originNoData') ||
		'No tenemos la rentabilidad de tu plan actual, así que no podemos proyectarlo.';
	// Mismo periodo: cada plan solo con comisión y rentabilidad, y el periodo y las fechas una
	// sola vez debajo (si no, la leyenda no cabe y el nombre del plan desaparece). Periodos
	// distintos: cada uno con el suyo.
	// Ciclo de vida que baja con la edad: « · baja con la edad» (decisión 14/10/2026).
	const stats = (plan: PlanReturnView) =>
		(samePeriod
			? legendStatsShort(translate, plan.managementFeePct, plan.annualPct)
			: legendStats(
					translate,
					plan.managementFeePct,
					plan.annualPct,
					plan.periodYears,
					periodRangeLabel(translate, plan.period)
				)) + ageNote(translate, plan);
	const accent = arcaBetter ? 'var(--primary)' : 'var(--destructive)';

	const arcaGeneric = translate('individual.transferProjection.arcaGeneric') || 'Plan de Arca';
	const currentGeneric =
		translate('individual.transferProjection.currentGeneric') || 'Tu plan actual';
	const arcaLegendName = arcaShortName || arcaPlanName || arcaGeneric;
	const currentLegendName = currentPlanName || currentGeneric;

	const fmtK = (n: number) => (n >= 1000 ? `${Math.round(n / 1000)}k` : `${n}`);
	const tickStep = Math.max(1, Math.round(years / 5));

	return (
		<div className="surface-card p-5 flex flex-col gap-4 h-full">
			<div>
				<p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
					{(
						translate('individual.transferProjection.header') ||
						'Próximos {years} años hasta tu jubilación'
					).replace('{years}', String(years))}
				</p>
				<h3 className="text-lg font-bold mt-1">
					{translate('individual.transferProjection.title') || '¿Qué pasaría a partir de hoy?'}
				</h3>
			</div>

			<div className="grid grid-cols-2 gap-3">
				<div className="surface-soft rounded-xl p-4">
					<p className="text-xs text-muted-foreground leading-tight">
						{/* Con nombre, como la tarjeta de Arca: así se ve qué plan es en todos los sitios. */}
						{(currentPlanName
							? (
									translate('individual.transferProjection.currentNamedIn') ||
									'Con {plan} en {years} años'
								).replace('{plan}', currentPlanName)
							: translate('individual.transferProjection.currentIn') ||
								'Con tu plan actual en {years} años'
						).replace('{years}', String(years))}
					</p>
					{origin ? (
						<p className="text-2xl font-bold font-heading mt-1">{fmtEur(currentFinal)}</p>
					) : (
						<p className="text-xs text-muted-foreground mt-1">{originNoData}</p>
					)}
				</div>
				<div
					className={
						arcaBetter
							? 'bg-primary/5 border border-primary/20 rounded-xl p-4'
							: 'bg-destructive/5 border border-destructive/20 rounded-xl p-4'
					}
				>
					<p className="text-xs text-muted-foreground leading-tight">
						{(translate('individual.transferProjection.arcaIn') || 'Con {plan} en {years} años')
							.replace('{plan}', arcaPlanName)
							.replace('{years}', String(years))}
					</p>
					<p
						className={`text-2xl font-bold font-heading mt-1 ${arcaBetter ? 'text-primary' : 'text-destructive'}`}
					>
						{fmtEur(arcaFinal)}
					</p>
				</div>
			</div>

			<div className="flex-1 min-h-[240px]">
				<ResponsiveContainer width="100%" height="100%" minHeight={240}>
					<AreaChart data={data} margin={{ top: 4, right: 6, left: 0, bottom: 0 }}>
						<defs>
							<linearGradient id="gradTransferProjection" x1="0" y1="0" x2="0" y2="1">
								<stop offset="5%" stopColor={accent} stopOpacity={0.15} />
								<stop offset="95%" stopColor={accent} stopOpacity={0} />
							</linearGradient>
						</defs>
						<XAxis
							dataKey="year"
							tickFormatter={(v) => {
								if (v === 0) return translate('individual.dashboard.comparison.today') || 'Hoy';
								if (v % tickStep === 0) return `Año ${v}`;
								return '';
							}}
							tick={{ fontSize: 10 }}
							height={16}
							tickMargin={2}
						/>
						<YAxis tickFormatter={fmtK} tick={{ fontSize: 10 }} width={36} />
						<Tooltip
							content={
								<CustomTooltip
									arcaName={{ full: arcaLegendName, generic: arcaGeneric }}
									currentName={{ full: currentLegendName, generic: currentGeneric }}
									arcaBetter={arcaBetter}
								/>
							}
						/>
						{origin && (
							<Area
								type="monotone"
								dataKey="bank"
								stroke="var(--muted-foreground)"
								strokeWidth={1.5}
								strokeDasharray="5 3"
								fill="none"
							/>
						)}
						<Area
							type="monotone"
							dataKey="plan"
							stroke={accent}
							strokeWidth={2}
							fill="url(#gradTransferProjection)"
						/>
					</AreaChart>
				</ResponsiveContainer>
			</div>

			{/* Cada plan en UNA línea en escritorio: el nombre se recorta, las cifras no. */}
			<div className="flex flex-col gap-1.5 text-xs">
				<div
					data-testid="legend-arca"
					className="flex items-center gap-1.5 min-w-0 flex-wrap sm:flex-nowrap"
				>
					<div
						className={`w-4 shrink-0 h-0.5 rounded ${arcaBetter ? 'bg-primary' : 'bg-destructive'}`}
					/>
					<span className="flex min-w-0 text-foreground">
						<SeriesLabel
							name={{ full: arcaLegendName, generic: arcaGeneric }}
							title={arcaPlanName}
						/>
					</span>
					<span data-stats className="sm:shrink-0 text-muted-foreground">
						({stats(arca)})
					</span>
				</div>
				<div
					data-testid="legend-current"
					className="flex items-center gap-1.5 min-w-0 flex-wrap sm:flex-nowrap"
				>
					<div className="w-4 shrink-0 border-t-2 border-dashed border-muted-foreground/60" />
					<span className="flex min-w-0 text-foreground">
						<SeriesLabel
							name={{ full: currentLegendName, generic: currentGeneric }}
							title={currentPlanName}
						/>
					</span>
					<span data-stats className="sm:shrink-0 text-muted-foreground">
						{origin ? `(${stats(origin)})` : originNoData}
					</span>
				</div>
			</div>

			{origin && (
				<p className="text-xs text-center font-medium text-foreground/70">
					{samePeriod
						? (
								translate('individual.transferProjection.returnPeriod') ||
								'Comparativa con la rentabilidad neta anualizada a {period}, el mismo periodo para ambos planes.'
							).replace(
								'{period}',
								periodWithRange(
									translate,
									arca.periodYears,
									periodRangeLabel(translate, arca.period)
								)
							)
						: translate('individual.transferProjection.returnPeriodDifferent') ||
							'No hay un periodo común: cada plan usa la rentabilidad anualizada del periodo que publica.'}
				</p>
			)}

			<p className="text-xs text-muted-foreground text-center">
				{translate('individual.transferProjection.disclaimer') ||
					'Rentabilidades netas: ya descuentan las comisiones de cada plan. Las rentabilidades pasadas no garantizan rentabilidades futuras.'}
			</p>
		</div>
	);
};

export default TransferProjectionCard;
